/* DynamicSection — renders any top-level section that isn't explicitly in the
 * schema. We detect the shape of the value and render appropriate form fields.
 * This lets the CMS expose EVERY existing field on a document without us
 * having to hand-author a schema entry for each one.
 *
 * Supported shapes:
 *   string                                   → textarea
 *   { title, body }                          → text + textarea
 *   { title, items: [string|object] }        → text + repeatable rows
 *   { title, body, label, href } (CTA-like)  → text + textarea + label/href
 *   { label, headline, body }                → text x3 (eyebrow header)
 *   list of strings                          → repeatable string rows
 *   list of {title, body}                    → repeatable rows
 *   other object                             → renders each sub-key as a text/
 *                                              textarea field
 */
import React from "react";
import { TextField, TextArea, RepeatableRows } from "./_fields";

function humanLabel(key) {
  return key
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();
}

function StringFieldRow({ label, value, onChange, rows }) {
  const isLong = (value && String(value).length > 80) || rows;
  return isLong ? (
    <TextArea label={label} value={value} onChange={onChange} rows={rows || 4} />
  ) : (
    <TextField label={label} value={value} onChange={onChange} />
  );
}

function ListOfStrings({ label, value, onChange }) {
  return (
    <RepeatableRows
      label={label}
      items={value || []}
      onChange={onChange}
      defaultItem=""
      addLabel="Add item"
      renderRow={(row, _u, i) => (
        <TextField
          label={`Item ${i + 1}`}
          value={row || ""}
          onChange={(v) => {
            const next = (value || []).slice();
            next[i] = v;
            onChange(next);
          }}
        />
      )}
    />
  );
}

function ListOfObjects({ label, value, onChange }) {
  return (
    <RepeatableRows
      label={label}
      items={value || []}
      onChange={onChange}
      defaultItem={{ title: "", body: "" }}
      addLabel="Add row"
      renderRow={(row, update) => {
        const keys = Object.keys(row || { title: "", body: "" });
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {keys.map((k) => {
              const v = row?.[k];
              const isLong = typeof v === "string" && v.length > 80;
              const wide = isLong || k === "body" || k === "description";
              return (
                <div key={k} className={wide ? "md:col-span-2" : ""}>
                  {wide ? (
                    <TextArea label={humanLabel(k)} value={v || ""} onChange={(nv) => update({ [k]: nv })} rows={3} />
                  ) : (
                    <TextField label={humanLabel(k)} value={v || ""} onChange={(nv) => update({ [k]: nv })} />
                  )}
                </div>
              );
            })}
          </div>
        );
      }}
    />
  );
}

function ObjectFields({ value, onChange }) {
  const obj = value && typeof value === "object" && !Array.isArray(value) ? value : {};
  const setKey = (k, v) => onChange({ ...obj, [k]: v });
  return (
    <div className="space-y-4">
      {Object.entries(obj).map(([k, v]) => {
        if (typeof v === "string") {
          return (
            <StringFieldRow
              key={k}
              label={humanLabel(k)}
              value={v}
              onChange={(nv) => setKey(k, nv)}
              rows={k === "body" || k === "description" || k === "subheadline" ? 4 : null}
            />
          );
        }
        if (Array.isArray(v)) {
          if (v.length === 0 || typeof v[0] === "string") {
            return <ListOfStrings key={k} label={humanLabel(k)} value={v} onChange={(nv) => setKey(k, nv)} />;
          }
          return <ListOfObjects key={k} label={humanLabel(k)} value={v} onChange={(nv) => setKey(k, nv)} />;
        }
        if (v && typeof v === "object") {
          return (
            <div key={k} className="border border-black/10 bg-white p-4 rounded-sm">
              <div className="bsca-eyebrow text-[#7A5A06] text-[10px] mb-2">{humanLabel(k)}</div>
              {React.createElement(ObjectFields, { value: v, onChange: (nv) => setKey(k, nv) })}
            </div>
          );
        }
        return (
          <TextField
            key={k}
            label={humanLabel(k)}
            value={v == null ? "" : String(v)}
            onChange={(nv) => setKey(k, nv)}
          />
        );
      })}
    </div>
  );
}

export default function DynamicSection({ k, value, onChange }) {
  const label = humanLabel(k);
  if (typeof value === "string" || value == null) {
    return (
      <div className="bg-white border border-black/10 p-5 rounded-sm">
        <StringFieldRow label={label} value={value || ""} onChange={onChange} rows={6} />
      </div>
    );
  }
  if (Array.isArray(value)) {
    return (
      <div className="bg-white border border-black/10 p-5 rounded-sm">
        {value.length === 0 || typeof value[0] === "string" ? (
          <ListOfStrings label={label} value={value} onChange={onChange} />
        ) : (
          <ListOfObjects label={label} value={value} onChange={onChange} />
        )}
      </div>
    );
  }
  return (
    <div className="bg-white border border-black/10 p-5 rounded-sm">
      <div className="bsca-eyebrow text-[#7A5A06] mb-3">{label}</div>
      <ObjectFields value={value} onChange={onChange} />
    </div>
  );
}

// Returns a sorted list of keys present on `doc` that are NOT in `excluded`.
export function dynamicKeys(doc, excluded) {
  if (!doc || typeof doc !== "object") return [];
  const skip = new Set([
    ...excluded,
    "id", "_id", "key", "slug", "status", "display_order", "created_at", "updated_at",
  ]);
  return Object.keys(doc).filter((k) => !skip.has(k));
}
